/**
 * MCP (Model Context Protocol) utility functions
 */

const log = () => {};
const warn = () => {};

/**
 * Parse MCP response (handles JSON and SSE formats)
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed response data
 */
export async function parseResponse(response) {
    const contentType = response.headers.get('Content-Type') || '';
    const text = await response.text();

    // Empty response
    if (!text || text.trim() === '') {
        throw new Error('Empty response');
    }

    // SSE format: "data: {...}\n\n"
    if (contentType.includes('event-stream') || text.startsWith('data: ')) {
        const lines = text.split('\n');
        for (const line of lines) {
            if (line.startsWith('data: ')) {
                return JSON.parse(line.slice(6));
            }
        }
    }

    // Regular JSON
    return JSON.parse(text);
}

/**
 * Discover tools from an MCP server
 * @param {Object} server - MCP server configuration
 * @returns {Promise<Array>} List of discovered tools
 */
export async function discoverMCPTools(server) {
    log(`🔍 [START] Discovering tools for ${server.name}`);

    // Handle headers in both array and object format
    let headersObj = {};
    if (server.headers) {
        if (Array.isArray(server.headers)) {
            // Array format: [{ key: 'x', value: 'y' }]
            headersObj = server.headers.reduce((acc, h) => {
                if (h.key && h.value) {
                    acc[h.key] = h.value;
                }
                return acc;
            }, {});
        } else if (typeof server.headers === 'object') {
            // Object format: { 'x': 'y' }
            headersObj = server.headers;
        }
    }

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/event-stream',
        ...headersObj
    };

    try {
        // ATTEMPT 1: Try direct tools/list (MCP 2024-11-05 spec)
        log(`📤 [ATTEMPT 1] Trying direct tools/list for ${server.name}...`);

        const directResponse = await fetch(`${server.url}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'tools/list',
                params: {}
            })
        });

        if (!directResponse.ok) {
            throw new Error(`HTTP ${directResponse.status}: ${directResponse.statusText}`);
        }

        const result = await parseResponse(directResponse);

        if (result.error) {
            warn(`⚠️ Direct tools/list returned error for ${server.name}: ${result.error.message}`);
            throw new Error(result.error.message);
        }

        if (result.result && Array.isArray(result.result.tools)) {
            log(`✅ Direct tools/list succeeded for ${server.name}`);
            return {
                tools: result.result.tools.map((tool) => ({
                    name: tool.name,
                    description: tool.description || tool.name,
                    source: server.name
                })),
                method: 'direct',
                requiresSession: false
            };
        }

        throw new Error('Invalid tools/list response format');
    } catch (directError) {
        warn(`⚠️ Direct method failed for ${server.name}: ${directError.message}`);
        log(`🔄 Falling back to session-based initialization...`);

        // FALLBACK: Session-based initialization
        log(`📤 [FALLBACK] Starting initialize for ${server.name}...`);

        const initResponse = await fetch(`${server.url}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 0,
                method: 'initialize',
                params: {
                    protocolVersion: '2024-11-05',
                    capabilities: {},
                    clientInfo: {
                        name: 'tpd-soe-agent',
                        version: '1.0.0'
                    }
                }
            })
        });

        if (!initResponse.ok) {
            throw new Error(`Initialize failed: HTTP ${initResponse.status}`);
        }

        log(`✓ Initialize response received (status: ${initResponse.status})`);

        const initResult = await parseResponse(initResponse);
        log(`✓ Initialize response parsed:`, initResult);

        // Extract session ID if available
        let sessionId = null;
        const initContentType = initResponse.headers.get('Content-Type') || '';

        if (initResult.result?.sessionId) {
            sessionId = initResult.result.sessionId;
            log(`✓ Session ID found in response body: ${sessionId}`);
        } else if (initResponse.headers.get('X-Session-Id')) {
            sessionId = initResponse.headers.get('X-Session-Id');
            log(`✓ Session ID found in headers: ${sessionId}`);
        } else {
            warn(`⚠️ Session ID NOT FOUND in headers or body`);
        }

        // Now call tools/list with session if available
        const toolsHeaders = { ...headers };
        if (sessionId) {
            toolsHeaders['X-Session-Id'] = sessionId;
        }

        const toolsResponse = await fetch(`${server.url}`, {
            method: 'POST',
            headers: toolsHeaders,
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'tools/list',
                params: {}
            })
        });

        if (!toolsResponse.ok) {
            throw new Error(`tools/list failed: HTTP ${toolsResponse.status}`);
        }

        const toolsResult = await parseResponse(toolsResponse);

        if (toolsResult.error) {
            throw new Error(`tools/list error: ${toolsResult.error.message}`);
        }

        if (toolsResult.result && Array.isArray(toolsResult.result.tools)) {
            log(`✅ Session-based tools/list succeeded for ${server.name}`);
            return {
                tools: toolsResult.result.tools.map((tool) => ({
                    name: tool.name,
                    description: tool.description || tool.name,
                    source: server.name
                })),
                method: 'session-based',
                requiresSession: true,
                sessionId: sessionId
            };
        }

        throw new Error('No tools found in response');
    }
}

export default { parseResponse, discoverMCPTools };
